import { useCallback, useEffect, useRef, useState } from 'react';

/** Teto padrão = o da mensagem de voz (backend: `DURACAO_MAXIMA_DA_MENSAGEM_DE_VOZ_SEGUNDOS`). */
export const DURACAO_MAXIMA_SEGUNDOS = 300;

/**
 * Ordem de preferência. Chrome/Firefox gravam WebM/Opus; o Safari só grava
 * MP4/AAC. São os dois formatos que os perfis de áudio do backend aceitam —
 * qualquer outro seria recusado com 415 no envio.
 */
const FORMATOS = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4'];

function formatoSuportado(): string | null {
  if (typeof MediaRecorder === 'undefined' || !navigator.mediaDevices?.getUserMedia) return null;
  return FORMATOS.find((formato) => MediaRecorder.isTypeSupported(formato)) ?? null;
}

export interface Gravacao {
  audio: Blob;
  duracaoSegundos: number;
  /** 1, 2, 3... Numa gravação sem trechos, é sempre 1. */
  ordem: number;
}

/** `silencio`: gravou, mas o microfone não captou som nenhum — o áudio NÃO é entregue. */
export type ErroDeGravacao = 'permissao-negada' | 'sem-microfone' | 'silencio' | 'falhou';

/**
 * RMS abaixo disto, durante a gravação INTEIRA, é microfone mudo/errado. O
 * piso de ruído de um microfone ligado numa sala quieta fica em ~0,001–0,005;
 * fala fica em ~0,02–0,3. Os áudios mudos vistos em teste tinham pico 0.
 *
 * Existe porque o modelo de transcrição, recebendo silêncio, "inventa" uma
 * frase (em teste: em polonês) — e o advisor receberia um áudio vazio com uma
 * transcrição falsa.
 */
const LIMIAR_DE_SILENCIO = 0.01;

/** Depois disso sem som, a barra de gravação avisa na hora — não só ao enviar. */
const SEGUNDOS_PARA_AVISAR_SEM_SOM = 2;

const INTERVALO_DE_MEDICAO_MS = 100;

/**
 * Medidor de volume do microfone (Web Audio). `null` onde não há
 * `AudioContext` (jsdom, navegador muito antigo): aí não se mede — e também
 * não se bloqueia nada por silêncio, porque não dá para saber.
 */
function criarMedidor(stream: MediaStream) {
  if (typeof AudioContext === 'undefined') return null;

  const contexto = new AudioContext();
  const analisador = contexto.createAnalyser();
  analisador.fftSize = 1024;
  contexto.createMediaStreamSource(stream).connect(analisador);
  const amostras = new Float32Array(analisador.fftSize);

  return {
    /** RMS do último trecho captado (0 = silêncio absoluto). */
    ler(): number {
      analisador.getFloatTimeDomainData(amostras);
      let soma = 0;
      for (const amostra of amostras) soma += amostra * amostra;
      return Math.sqrt(soma / amostras.length);
    },
    fechar() {
      void contexto.close().catch(() => {});
    },
  };
}

interface Opcoes {
  /** Chamado quando a gravação bate o teto e para sozinha, com o ÚLTIMO trecho — o chamador decide se envia. */
  aoAtingirLimite: (gravacao: Gravacao) => void;
  aoFalhar: (erro: ErroDeGravacao) => void;
  /** Padrão: `DURACAO_MAXIMA_SEGUNDOS` (mensagem de voz). A consulta gravada usa 90 min. */
  duracaoMaximaSegundos?: number;
  /**
   * Fecha um trecho a cada N segundos GRAVADOS (pausas não contam) e entrega
   * em `aoTrecho`, sem parar a gravação. Omitido = gravação inteira num áudio
   * só (mensagem de voz). A consulta gravada usa trechos para enviar DURANTE a
   * consulta: uma aba fechada no meio perde no máximo o trecho em andamento.
   */
  trechoSegundos?: number;
  aoTrecho?: (gravacao: Gravacao) => void;
}

/**
 * Gravação de voz no navegador — mensagem de voz da discussão e consulta gravada.
 *
 * O tempo é contado AQUI (não lido do áudio): o WebM do `MediaRecorder` sai
 * sem duração no cabeçalho — `audio.duration` dá `Infinity` — e é esse número
 * que vai para o backend e aparece no player.
 */
export function useGravadorDeAudio({
  aoAtingirLimite,
  aoFalhar,
  duracaoMaximaSegundos = DURACAO_MAXIMA_SEGUNDOS,
  trechoSegundos,
  aoTrecho,
}: Opcoes) {
  const [gravando, setGravando] = useState(false);
  const [pausado, setPausado] = useState(false);
  /** Tempo GRAVADO — pausas fora. */
  const [segundos, setSegundos] = useState(0);
  /** Volume atual, 0–1, para o medidor da barra de gravação. */
  const [nivel, setNivel] = useState(0);
  /** Já passou `SEGUNDOS_PARA_AVISAR_SEM_SOM` e o microfone não captou nada. */
  const [semSom, setSemSom] = useState(false);
  const suportado = formatoSuportado() !== null;

  const formatoRef = useRef<string | null>(null);
  const gravadorRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const medidorRef = useRef<ReturnType<typeof criarMedidor>>(null);
  const maiorNivelRef = useRef(0);
  const relogioRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Relógio de tempo GRAVADO: o acumulado até a última pausa + o trecho corrido desde o último início/retomada.
  const acumuladoMsRef = useRef(0);
  const retomadoEmRef = useRef(0);
  const pausadoRef = useRef(false);
  const ordemRef = useRef(0);

  // O `onstop` do MediaRecorder é assíncrono: quem pediu o FIM da gravação espera aqui.
  const aoPararRef = useRef<((gravacao: Gravacao | null, silenciosa: boolean) => void) | null>(null);
  const opcoesRef = useRef({ aoAtingirLimite, aoFalhar, aoTrecho, duracaoMaximaSegundos, trechoSegundos });
  opcoesRef.current = { aoAtingirLimite, aoFalhar, aoTrecho, duracaoMaximaSegundos, trechoSegundos };

  const tempoGravadoMs = () =>
    acumuladoMsRef.current + (pausadoRef.current ? 0 : Date.now() - retomadoEmRef.current);

  const liberarMicrofone = useCallback(() => {
    if (relogioRef.current) clearInterval(relogioRef.current);
    relogioRef.current = null;
    medidorRef.current?.fechar();
    medidorRef.current = null;
    streamRef.current?.getTracks().forEach((trilha) => trilha.stop());
    streamRef.current = null;
    gravadorRef.current = null;
    pausadoRef.current = false;
    setGravando(false);
    setPausado(false);
    setSegundos(0);
    setNivel(0);
    setSemSom(false);
  }, []);

  /**
   * Um `MediaRecorder` por trecho, todos sobre o MESMO stream do microfone.
   * `final`: é o trecho que encerra a gravação (resolve `aoPararRef`); os
   * demais saem por `aoTrecho` e a gravação segue.
   */
  const criarGravadorDoTrecho = useCallback(
    (stream: MediaStream, formato: string) => {
      const gravador = new MediaRecorder(stream, { mimeType: formato });
      const pedacos: Blob[] = [];
      const ordem = ++ordemRef.current;
      // Início e fim DESTE trecho, em tempo gravado. Guardados aqui, e não lidos
      // de um ref compartilhado no `onstop`: na rotação o `onstop` chega depois
      // de o próximo trecho já ter começado, e mediria o trecho errado.
      const inicioMs = tempoGravadoMs();
      let fimMs: number | null = null;
      let final = false;

      gravador.ondataavailable = (evento) => {
        if (evento.data.size > 0) pedacos.push(evento.data);
      };
      gravador.onstop = () => {
        const duracaoSegundos = Math.min(
          opcoesRef.current.duracaoMaximaSegundos,
          Math.max(1, Math.round(((fimMs ?? tempoGravadoMs()) - inicioMs) / 1000)),
        );
        // O tipo do Blob sem o `;codecs=` — é o que vira o Content-Type do upload.
        const gravacao: Gravacao = { audio: new Blob(pedacos, { type: formato.split(';')[0] }), duracaoSegundos, ordem };

        if (!final) {
          // Rotação: o próximo trecho já está gravando desde antes deste parar.
          opcoesRef.current.aoTrecho?.(gravacao);
          return;
        }

        // Lido ANTES de `liberarMicrofone`, que descarta o medidor.
        const silenciosa = medidorRef.current !== null && maiorNivelRef.current < LIMIAR_DE_SILENCIO;
        liberarMicrofone();
        aoPararRef.current?.(gravacao, silenciosa);
        aoPararRef.current = null;
      };
      gravador.onerror = () => {
        liberarMicrofone();
        aoPararRef.current?.(null, false);
        aoPararRef.current = null;
        opcoesRef.current.aoFalhar('falhou');
      };

      return {
        gravador,
        inicioMs,
        encerrar(ehFinal: boolean) {
          final = ehFinal;
          fimMs = tempoGravadoMs();
          if (gravador.state !== 'inactive') gravador.stop();
        },
      };
    },
    [liberarMicrofone],
  );

  const trechoAtualRef = useRef<ReturnType<typeof criarGravadorDoTrecho> | null>(null);

  /** Fecha o trecho atual e começa o próximo — o novo começa ANTES do antigo parar, sem buraco no áudio. */
  const rotacionarTrecho = useCallback(() => {
    const stream = streamRef.current;
    const formato = formatoRef.current;
    const anterior = trechoAtualRef.current;
    if (!stream || !formato || !anterior) return;

    const proximo = criarGravadorDoTrecho(stream, formato);
    proximo.gravador.start();
    // O trecho que sai termina aqui — e cada um guarda o próprio início/fim.
    anterior.encerrar(false);
    trechoAtualRef.current = proximo;
    gravadorRef.current = proximo.gravador;
  }, [criarGravadorDoTrecho]);

  /**
   * Para e devolve o ÚLTIMO trecho (numa gravação sem trechos, o áudio todo) —
   * `null` se não havia gravação, se `descartar` for true, ou se a gravação
   * saiu muda (aí avisa com `aoFalhar('silencio')`).
   */
  const parar = useCallback(
    (descartar: boolean): Promise<Gravacao | null> => {
      const trecho = trechoAtualRef.current;
      if (!trecho || trecho.gravador.state === 'inactive') {
        liberarMicrofone();
        return Promise.resolve(null);
      }

      if (relogioRef.current) clearInterval(relogioRef.current);
      relogioRef.current = null;

      return new Promise((resolve) => {
        aoPararRef.current = (gravacao, silenciosa) => {
          if (descartar || !gravacao) return resolve(null);
          if (silenciosa) {
            opcoesRef.current.aoFalhar('silencio');
            return resolve(null);
          }
          resolve(gravacao);
        };
        // Pausado, o `stop` ainda entrega o que foi gravado até a pausa.
        trecho.encerrar(true);
        trechoAtualRef.current = null;
      });
    },
    [liberarMicrofone],
  );

  const iniciar = useCallback(async () => {
    const formato = formatoSuportado();
    if (!formato || trechoAtualRef.current) return;

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (erro) {
      const nome = erro instanceof DOMException ? erro.name : '';
      opcoesRef.current.aoFalhar(
        nome === 'NotAllowedError' || nome === 'SecurityError'
          ? 'permissao-negada'
          : nome === 'NotFoundError'
            ? 'sem-microfone'
            : 'falhou',
      );
      return;
    }

    streamRef.current = stream;
    formatoRef.current = formato;
    maiorNivelRef.current = 0;
    ordemRef.current = 0;
    acumuladoMsRef.current = 0;
    pausadoRef.current = false;
    try {
      medidorRef.current = criarMedidor(stream);
    } catch {
      // Sem medidor a gravação segue normal — só não há medidor nem trava de silêncio.
      medidorRef.current = null;
    }

    // Relógio zerado ANTES do primeiro trecho: ele lê o tempo gravado ao nascer.
    retomadoEmRef.current = Date.now();
    const trecho = criarGravadorDoTrecho(stream, formato);
    trechoAtualRef.current = trecho;
    gravadorRef.current = trecho.gravador;
    trecho.gravador.start();
    setGravando(true);
    setPausado(false);
    setSegundos(0);

    relogioRef.current = setInterval(() => {
      if (pausadoRef.current) return;

      const medidor = medidorRef.current;
      if (medidor) {
        const atual = medidor.ler();
        maiorNivelRef.current = Math.max(maiorNivelRef.current, atual);
        // RMS de fala raramente passa de ~0,3: escala para o medidor encher.
        setNivel(Math.min(1, atual * 4));
      }

      const gravadoMs = tempoGravadoMs();
      const decorridos = Math.floor(gravadoMs / 1000);
      setSegundos(decorridos);
      setSemSom(
        medidor !== null &&
          decorridos >= SEGUNDOS_PARA_AVISAR_SEM_SOM &&
          maiorNivelRef.current < LIMIAR_DE_SILENCIO,
      );

      const { duracaoMaximaSegundos: teto, trechoSegundos: tamanhoDoTrecho } = opcoesRef.current;
      if (decorridos >= teto) {
        void parar(false).then((gravacao) => {
          if (gravacao) opcoesRef.current.aoAtingirLimite(gravacao);
        });
        return;
      }
      const trechoAtual = trechoAtualRef.current;
      if (tamanhoDoTrecho && trechoAtual && gravadoMs - trechoAtual.inicioMs >= tamanhoDoTrecho * 1000) {
        rotacionarTrecho();
      }
    }, INTERVALO_DE_MEDICAO_MS);
  }, [criarGravadorDoTrecho, parar, rotacionarTrecho]);

  /** O tempo pausado não conta — nem no cronômetro, nem no teto, nem no tamanho do trecho. */
  const pausar = useCallback(() => {
    const gravador = gravadorRef.current;
    if (!gravador || gravador.state !== 'recording') return;
    gravador.pause();
    acumuladoMsRef.current += Date.now() - retomadoEmRef.current;
    pausadoRef.current = true;
    setPausado(true);
    setNivel(0);
  }, []);

  const retomar = useCallback(() => {
    const gravador = gravadorRef.current;
    if (!gravador || gravador.state !== 'paused') return;
    retomadoEmRef.current = Date.now();
    pausadoRef.current = false;
    gravador.resume();
    setPausado(false);
  }, []);

  // Sair da tela no meio da gravação não pode deixar o microfone ligado.
  // `encerrar(true)` e não `stop()` cru: o trecho sai pelo caminho de FIM, com
  // ninguém esperando (`aoPararRef` nulo) — e não por `aoTrecho`, que dispararia
  // um envio depois de a tela já ter fechado.
  useEffect(
    () => () => {
      aoPararRef.current = null;
      trechoAtualRef.current?.encerrar(true);
      trechoAtualRef.current = null;
      liberarMicrofone();
    },
    [liberarMicrofone],
  );

  return {
    suportado,
    gravando,
    pausado,
    segundos,
    nivel,
    semSom,
    iniciar,
    pausar,
    retomar,
    finalizar: () => parar(false),
    cancelar: () => parar(true),
  };
}

export function formatarDuracao(totalSegundos: number): string {
  const s = Math.max(0, Math.floor(totalSegundos));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}
